import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { auth } from "../firebase";
import Papa from "papaparse";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { updateMasterMetadata } from "../utils/cacheManager";

function SkuMaster({ role }) {
  const isReadOnly = role === "operator";
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [skus, setSkus] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [officialClientId, setOfficialClientId] = useState("");
  
  // Bulk States
  const [showBulk, setShowBulk] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  const [form, setForm] = useState({
    clientId: "",
    ItemCode: "",
    sku: "",
    Brand: "",
    Name: "",
    description: "",
    Category: "",
    msku: "",
    segment: "",
    hsn: "",
    Basic_Eway: "",
    gst: "",
    cess: "",
    addlcess: "",
    Rebate: "",
    openingstock: "",
    skutype: "",
    mrp: "",
    sortorder: "",
    swdorder: "",
    minQty: "",
    unitsPerOuter: "1.0",
    isLocked: false,
  });

  // Load SKUs
  const fetchSkus = async () => {
    const q = query(
      collection(db, "skuMaster"),
      where("clientId", "==", officialClientId)
    );
    const querySnapshot = await getDocs(q);
    const list = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setSkus(list);
  };

  // Fetch Official Client ID
  const fetchClientId = async () => {
    try {
      const snap = await getDoc(doc(db, "companyInfo", "default"));
      if (snap.exists()) {
        const cid = snap.data().clientId || "";
        setOfficialClientId(cid);
        setForm(f => ({ ...f, clientId: cid }));
      }
    } catch (err) {
      console.error("Error fetching clientId:", err);
    }
  };

  useEffect(() => {
    fetchClientId();
  }, []);

  useEffect(() => {
    if (officialClientId) {
      fetchSkus();
    }
  }, [officialClientId]);

  // Handle form change
  const updateForm = (e) => {
    if (e.target.name === "clientId") return;
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Save SKU
  const saveSku = async () => {
    try {
      // --- DUPLICATE VALIDATION ---
      const isDuplicateSort = skus.some(s => s.id !== editingId && String(s.sortorder) === String(form.sortorder) && form.sortorder !== "");
      const isDuplicateSwd = skus.some(s => s.id !== editingId && String(s.swdorder) === String(form.swdorder) && form.swdorder !== "");

      if (isDuplicateSort) {
        alert(`❌ DUPLICATE ERROR: Sort Order "${form.sortorder}" is already assigned to another SKU. Please use a unique number.`);
        return;
      }
      if (isDuplicateSwd) {
        alert(`❌ DUPLICATE ERROR: SWD Order "${form.swdorder}" is already assigned to another SKU. Please use a unique number.`);
        return;
      }

      if (!form.unitsPerOuter || form.unitsPerOuter === "") {
        alert("❌ VALIDATION ERROR: Please select a value for 'M PER OUTER'. This field is mandatory.");
        return;
      }

      if (!form.minQty || form.minQty === "") {
        alert("❌ VALIDATION ERROR: Please select a value for 'MIN QTY'. This field is mandatory.");
        return;
      }

      const payload = {
        ...form,
        clientId: officialClientId,
        Basic_Eway: Number(form.Basic_Eway || 0),
        gst: Number(form.gst || 0),
        cess: Number(form.cess || 0),
        addlcess: Number(form.addlcess || 0),
        Rebate: Number(form.Rebate || 0),
        openingstock: Number(form.openingstock || 0),
        mrp: Number(form.mrp || 0),
        sortorder: Number(form.sortorder || 0),
        swdorder: Number(form.swdorder || 0),
        minQty: Number(form.minQty || 0),
        unitsPerOuter: Number(form.unitsPerOuter || 1.0),
        isLocked: !!form.isLocked,
      };

      if (editingId) {
        await updateDoc(doc(db, "skuMaster", editingId), payload);
      } else {
        await addDoc(collection(db, "skuMaster"), payload);
      }
      setForm({
        clientId: officialClientId,
        ItemCode: "", sku: "", Brand: "", Name: "", description: "", msku: "",
        segment: "", hsn: "", Basic_Eway: "", gst: "40", cess: "", addlcess: "", Rebate: "",
        openingstock: "", skutype: "", unitType: "THD", mrp: "", sortorder: "", swdorder: "", minQty: "",
        unitsPerOuter: "",
        isLocked: false,
      });

      setEditingId(null);
	  await updateMasterMetadata(db, "sku");
      fetchSkus();
    } catch (err) {
      console.error("Error saving SKU:", err);
    }
  };

  // Edit SKU
  const editSku = (sku) => {
    setEditingId(sku.id);
    const { id, ...rest } = sku;
    setForm({
      ...rest,
      unitsPerOuter: rest.unitsPerOuter || "1.0",
      isLocked: !!rest.isLocked
    });
  };

  // Delete SKU
  const removeSku = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SKU?")) return;
    await deleteDoc(doc(db, "skuMaster", id));
	await updateMasterMetadata(db, "sku");
    fetchSkus();
  };

  // --- BULK OPERATIONS ---
  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").map((x) => x.trim()).filter(Boolean);
      const headers = lines[0].split(",");
      const rows = lines.slice(1).map((line) => {
        const cols = line.split(",");
        const obj = {};
        headers.forEach((h, i) => {
          obj[h.trim()] = cols[i] ? cols[i].trim() : "";
        });
        return obj;
      });
      setCsvData(rows);
    };
    reader.readAsText(file);
  };

  const confirmUpload = async () => {
    setBulkLoading(true);
    try {
      for (const row of csvData) {
        await addDoc(collection(db, "skuMaster"), {
          clientId: officialClientId,
          ItemCode: row.ItemCode || "",
          sku: row.sku || "",
          Brand: row.Brand || "",
          Name: row.Name || "",
          description: row.description || "",
          Category: row.Category || "CIGARETTES",
          msku: row.msku || "",
          segment: row.segment || "",
          hsn: row.hsn || "",
          Basic_Eway: Number(row.Basic_Eway || 0),
          gst: Number(row.gst || 0),
          cess: Number(row.cess || 0),
          addlcess: Number(row.addlcess || 0),
          Rebate: Number(row.Rebate || 0),
          openingstock: Number(row.openingstock || 0),
          mrp: Number(row.mrp || 0),
          skutype: row.skutype || "",
          sortorder: Number(row.sortorder || 0),
          swdorder: Number(row.swdorder || 0),
          minQty: Number(row.minQty || 0),
          unitsPerOuter: Number(row.unitsPerOuter || 1.0),
          isLocked: row.isLocked === "true" || row.isLocked === "1" || row.isLocked === "yes",
        });
      }
      alert("Batch upload successful!");
      setCsvData([]);
      setShowBulk(false);
      fetchSkus();
      await updateMasterMetadata(db, "sku");
    } catch (err) {
      console.error(err);
      alert("Error during upload.");
    }
    setBulkLoading(false);
  };

  const downloadTemplate = () => {
    const headers = "clientId,ItemCode,sku,Brand,Name,description,Category,msku,segment,hsn,mrp,Basic_Eway,gst,cess,addlcess,Rebate,openingstock,skutype,sortorder,swdorder,minQty,unitsPerOuter,isLocked";
    const sampleRow = `${officialClientId},1001,Sample SKU,Sample Brand,Sample Name,Sample Desc,CIGARETTES,MSKU01,Segment1,1234,100,80,18,0,0,0,10,PCS,1,1,1,1.0,false`;
    const blob = new Blob([headers + "\n" + sampleRow], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sku_master_template.csv";
    a.click();
  };

  const exportAll = () => {
    const headers = "clientId,ItemCode,sku,Brand,Name,description,Category,msku,segment,hsn,mrp,Basic_Eway,gst,cess,addlcess,Rebate,openingstock,skutype,sortorder,swdorder,minQty,unitsPerOuter,isLocked";
    const rows = skus.map((s) => [
      officialClientId, s.ItemCode || "", s.sku || "", s.Brand || "", s.Name || "",
      s.description || "", s.Category || "CIGARETTES", s.msku || "", s.segment || "", s.hsn || "", s.mrp || 0,
      s.Basic_Eway || 0, s.gst || 0, s.cess || 0, s.addlcess || 0, s.Rebate || 0,
      s.openingstock || 0, s.skutype || "", s.sortorder || 0, s.swdorder || 0, s.minQty || 0, s.unitsPerOuter || 1.0, !!s.isLocked
    ].join(","));
    const csvContent = [headers, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sku_master_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const filteredSkus = skus.filter((s) => {
    const term = search.toLowerCase();
    return (
      s.clientId?.toLowerCase().includes(term) ||
      s.ItemCode?.toLowerCase().includes(term) ||
      s.sku?.toLowerCase().includes(term) ||
      s.Brand?.toLowerCase().includes(term) ||
      s.Name?.toLowerCase().includes(term) ||
      s.description?.toLowerCase().includes(term) ||
      s.msku?.toLowerCase().includes(term) ||
      s.segment?.toLowerCase().includes(term) ||
      s.hsn?.toLowerCase().includes(term) ||
      String(s.mrp)?.toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          
          {/* HEADER & TOP ACTIONS */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32, flexWrap: "wrap", gap: 20 }}>
            <div style={{ borderLeft: "6px solid #4f46e5", paddingLeft: 20 }}>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
                SKU Master
              </h1>
              <p style={{ marginTop: 4, fontSize: 16, color: "#64748b", fontWeight: 500 }}>
                Manage your product catalog and financial parameters
              </p>
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={exportAll} style={{ ...secondaryActionStyle, background: "#10b981", color: "#fff" }}>
                📊 Export All
              </button>
              <button onClick={() => setShowBulk(!showBulk)} style={{ ...primaryActionStyle, background: showBulk ? "#f1f5f9" : "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)", color: showBulk ? "#475569" : "#fff"  }}>
                {showBulk ? "✖ Close Bulk Mode" : "📦 Bulk Operations"}
              </button>
            </div>
          </div>

          {/* BULK MANAGEMENT DASHBOARD */}
          {showBulk && !isReadOnly && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 350px", gap: 32, marginBottom: 40, alignItems: "start" }}>
              <div style={{ background: "#ffffff", borderRadius: 16, padding: "32px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #4f46e5" }}>
                <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: "18px", fontWeight: 800, color: "#4f46e5" }}>CSV BATCH UPLOAD</h3>
                <div style={{ background: "#f8fafc", border: "2px dashed #e2e8f0", borderRadius: 12, padding: "40px 20px", textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: "40px", marginBottom: "16px" }}>📄</div>
                  <input type="file" accept=".csv" onChange={handleCSV} style={{ fontSize: "14px", color: "#4f46e5", cursor: "pointer" }} />
                </div>

                {csvData.length > 0 && (
                  <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>AUDIT PREVIEW ({csvData.length} RECORDS)</h3>
                    <div style={{ maxHeight: "250px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 24 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
                          <tr style={{ textAlign: "left" }}>
                            {Object.keys(csvData[0] || {}).map((h) => <th key={h} style={tableHeaderStyle}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {csvData.slice(0, 10).map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              {Object.values(row).map((val, i) => <td key={i} style={{ padding: "12px 16px" }}>{val}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvData.length > 10 && <div style={{ fontSize: "11px", padding: "12px", textAlign: "center", color: "#94a3b8", background: "#f8fafc", fontWeight: 600 }}>... {csvData.length - 10} more records ready</div>}
                    </div>
                    <button onClick={confirmUpload} disabled={bulkLoading} style={{ ...primaryActionStyle, width: "100%", height: "50px" }}>
                      {bulkLoading ? "⚙️ SYNCING CATALOG..." : `✅ COMMIT ${csvData.length} RECORDS TO MASTER`}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ background: "#1e293b", borderRadius: 16, padding: "24px", color: "#f8fafc", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
                <h3 style={{ marginTop: 0, fontSize: "12px", color: "#818cf8", letterSpacing: "0.1em", fontWeight: 800, textTransform: "uppercase" }}>FORMAT GUIDE</h3>
                <p style={{ fontSize: "13px", color: "#94a3b8", lineHeight: "1.6", margin: "12px 0" }}>Ensure your column headers match exactly to prevent sync errors.</p>
                <div style={{ background: "rgba(255,255,255,0.05)", padding: "12px", borderRadius: 8, fontFamily: "monospace", fontSize: "10px", wordBreak: "break-all" }}>
                  clientId, ItemCode, sku, Brand, Name, description, msku, segment, hsn, mrp, Basic_Eway, gst, cess, addlcess, Rebate, openingstock, skutype, sortorder, swdorder, minQty, unitsPerOuter, isLocked
                </div>
                <button onClick={downloadTemplate} style={{ ...secondaryActionStyle, width: "100%", marginTop: 16, background: "rgba(255,255,255,0.1)", color: "#fff" }}>
                  📥 Download Template
                </button>
              </div>
            </div>
          )}

          {/* FORM CARD (ONLY IF NOT READ ONLY & NOT IN BULK MODE) */}
          {!isReadOnly && !showBulk && (
            <div style={{ background: "#ffffff", borderRadius: 16, padding: "32px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", marginBottom: 40 }}>
              <h3 style={{ marginTop: 0, marginBottom: 24, fontSize: "18px", fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: "10px" }}>
                {editingId ? "✏️ Edit Product Details" : "🆕 Register New SKU"}
              </h3>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px 30px" }}>
                {[
                  ["clientId", "Client ID", "mdsons"],
                  ["ItemCode", "Item Code", "EX: 1001"],
                  ["sku", "SKU Name", "Full product name"],
                  ["Brand", "Brand", "Brand name"],
                  ["Name", "Display Name", "Short name"],
                  ["description", "Description", "Details"],
                  ["Category", "Product Category", "e.g. CIGARETTES"],
                  ["msku", "MSKU", "Master SKU"],
                ].map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    {key === "clientId" ? (
                      <div style={{ ...inputStyle, background: "#f1f5f9", fontWeight: 700, color: "#4f46e5" }}>
                        {officialClientId} 
                        <span style={{ fontSize: "10px", color: "#94a3b8", marginLeft: "8px", fontWeight: "normal" }}>(AUTO)</span>
                      </div>
                    ) : (
                      <input
                        style={inputStyle}
                        name={key}
                        value={form[key]}
                        onChange={updateForm}
                        placeholder={placeholder}
                      />
                    )}
                  </div>
                ))}

                <div key="hsn">
                  <label style={labelStyle}>HSN Code</label>
                  <select
                    style={inputStyle}
                    name="hsn"
                    value={form.hsn}
                    onChange={(e) => {
                      const hsn = e.target.value;
                      const mapping = {
                        "24022090": "KSFT",
                        "24022050": "LONGS",
                        "24022040": "RSFT",
                        "24022030": "DSFT"
                      };
                      setForm(prev => ({
                        ...prev,
                        hsn: hsn,
                        segment: mapping[hsn] || prev.segment
                      }));
                    }}
                  >
                    <option value="">Select HSN</option>
                    <option value="24022090">24022090</option>
                    <option value="24022050">24022050</option>
                    <option value="24022040">24022040</option>
                    <option value="24022030">24022030</option>
                  </select>
                </div>

                <div key="segment">
                  <label style={labelStyle}>Segment</label>
                  <input
                    style={{ ...inputStyle, background: "#f8fafc" }}
                    name="segment"
                    value={form.segment}
                    onChange={updateForm}
                    placeholder="Auto-filled by HSN"
                  />
                </div>

                {[
                  ["Basic_Eway", "Basic (E-Way)", "Basic price"],
                  ["gst", "GST %", "18"],
                  ["openingstock", "Opening Stock", "0"],
                ].map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    {key === "clientId" ? (
                      <div style={{ ...inputStyle, background: "#f1f5f9", fontWeight: 700, color: "#4f46e5" }}>
                        {officialClientId} 
                        <span style={{ fontSize: "10px", color: "#94a3b8", marginLeft: "8px", fontWeight: "normal" }}>(AUTO)</span>
                      </div>
                    ) : (
                      <input
                        style={inputStyle}
                        name={key}
                        value={form[key]}
                        onChange={updateForm}
                        placeholder={placeholder}
                      />
                    )}
                  </div>
                ))}

                {[
                  ["skutype", "SKU Type", "PCS/LTR"],
                ].map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    <input
                      style={inputStyle}
                      name={key}
                      value={form[key]}
                      onChange={updateForm}
                      placeholder={placeholder}
                    />
                  </div>
                ))}

                <div key="unitType">
                  <label style={labelStyle}>Unit Type (GST UQC)</label>
                  <select
                    style={inputStyle}
                    name="unitType"
                    value={form.unitType || "THD"}
                    onChange={updateForm}
                  >
                    <option value="THD">THD (Thousands)</option>
                    <option value="PCS">PCS (Pieces)</option>
                    <option value="LTR">LTR (Litres)</option>
                    <option value="BOX">BOX (Box)</option>
                    <option value="KGS">KGS (Kilograms)</option>
                    <option value="NOS">NOS (Numbers)</option>
                  </select>
                </div>

                {[
                  ["mrp", "MRP", "0"],
                  ["sortorder", "Sort Order", "1"],
                  ["swdorder", "SWD Order", "1"],
                ].map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    <input
                      style={inputStyle}
                      name={key}
                      value={form[key]}
                      onChange={updateForm}
                      placeholder={placeholder}
                    />
                  </div>
                ))}

                <div key="minQty">
                  <label style={labelStyle}>Min Qty</label>
                  <select
                    style={inputStyle}
                    name="minQty"
                    value={form.minQty}
                    onChange={updateForm}
                  >
                    <option value="">Select Qty</option>
                    <option value="0.01">0.01</option>
                    <option value="0.02">0.02</option>
                    <option value="0.012">0.012</option>
                    <option value="0.016">0.016</option>
                  </select>
                </div>

                <div key="unitsPerOuter">
                  <label style={labelStyle}>M Per Outer</label>
                  <select
                    style={inputStyle}
                    name="unitsPerOuter"
                    value={form.unitsPerOuter}
                    onChange={updateForm}
                  >
                    <option value="">Select Qty</option>
                    <option value="0.16">0.16</option>
                    <option value="0.2">0.2</option>
                    <option value="0.24">0.24</option>
                    <option value="0.5">0.5</option>
                  </select>
                </div>

                {/* IS LOCKED TOGGLE */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px", background: "#f8f9fa", borderRadius: "10px", border: "1px solid #e9ecef" }}>
                  <input
                    type="checkbox"
                    id="isLocked"
                    name="isLocked"
                    checked={form.isLocked}
                    onChange={(e) => setForm({ ...form, isLocked: e.target.checked })}
                    style={{ width: "24px", height: "24px", cursor: "pointer" }}
                  />
                  <label htmlFor="isLocked" style={{ fontSize: "14px", fontWeight: "800", color: "#e03131", cursor: "pointer" }}>
                    🔒 LOCK FOR SALESMAN
                  </label>
                </div>
              </div>

              <div style={{ marginTop: 32, display: "flex", gap: 12 }}>
                <button onClick={saveSku} style={{ ...primaryActionStyle, width: "100%", height: "50px", fontSize: "16px" }}>
                  {editingId ? "💾 UPDATE PRODUCT RECORD" : "✅ SAVE NEW SKU"}
                </button>
                {editingId && (
                   <button onClick={() => {
                     setEditingId(null);
                     setForm({
                       clientId: "", ItemCode: "", sku: "", Brand: "", Name: "", description: "", msku: "",
                       segment: "", hsn: "", Basic_Eway: "", gst: "", cess: "", addlcess: "", Rebate: "",
                       openingstock: "", skutype: "", mrp: "", sortorder: "", swdorder: "", minQty: "",
                       unitsPerOuter: "1.0",
                       isLocked: false,
                     });
                   }} style={{ ...secondaryActionStyle, padding: "0 24px" }}>
                     Cancel
                   </button>
                )}
              </div>
            </div>
          )}

          {/* CATALOG TABLE */}
          <div style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>PRODUCT CATALOG ({filteredSkus.length})</h3>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Search SKU list..."
                style={{ ...inputStyle, width: "300px", padding: "10px 16px" }}
              />
            </div>

            <div style={{ overflowX: "auto", maxHeight: "65vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={tableHeaderStyle}>SKU NAME</th>
                    <th style={tableHeaderStyle}>BRAND</th>
                    <th style={tableHeaderStyle}>SEGMENT</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>MRP</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>BASIC</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>GST</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>STOCK</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>OUTER</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkus.map((s, idx) => (
                    <tr key={s.id} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#ffffff" : "#fafafa", transition: "background 0.2s" }}>
                      <td style={{ padding: "16px 24px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <div style={{ fontWeight: 800, color: "#1e293b" }}>{s.sku}</div>
                          {s.isLocked && <span title="Locked for Salesman">🔒</span>}
                        </div>
                        <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: 2 }}>{s.ItemCode} | {s.hsn}</div>
                      </td>
                      <td style={{ padding: "16px 24px", color: "#64748b", fontWeight: 600 }}>{s.Brand}</td>
                      <td style={{ padding: "16px 24px" }}>
                        <span style={{ fontSize: "10px", background: "#f1f5f9", color: "#475569", padding: "4px 8px", borderRadius: 4, fontWeight: 800 }}>{s.segment}</span>
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: 700, color: "#1e293b" }}>
                        ₹ {Number(s.mrp || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right", color: "#4f46e5", fontWeight: 700 }}>
                        ₹ {Number(s.Basic_Eway || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right", color: "#64748b" }}>{s.gst}%</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: 700, color: s.openingstock < 5 ? "#ef4444" : "#10b981" }}>
                        {s.openingstock} <span style={{ fontSize: "10px", color: "#94a3b8" }}>{s.skutype}</span>
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: 700, color: "#1e293b" }}>
                        {s.unitsPerOuter || 1.0}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right" }}>
                        {!isReadOnly && (
                          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => editSku(s)} style={tableActionBtnStyle}>Edit</button>
                            <button onClick={() => removeSku(s.id)} style={{ ...tableActionBtnStyle, color: "#ef4444" }}>Del</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredSkus.length === 0 && (
                    <tr>
                      <td colSpan="9" style={{ padding: 100, textAlign: "center" }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🔎</div>
                        <h3 style={{ color: "#1e293b", margin: 0 }}>No SKUs Found</h3>
                        <p style={{ color: "#64748b", marginTop: 8 }}>Try adjusting your search terms or register a new SKU.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const primaryActionStyle = {
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.3)",
};

const secondaryActionStyle = {
  ...primaryActionStyle,
  width: "auto",
  background: "#f1f5f9",
  color: "#475569",
  boxShadow: "none",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  fontSize: "14px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 800,
  color: "#94a3b8",
  marginBottom: "8px",
  letterSpacing: "0.05em",
  textTransform: "uppercase"
};

const tableHeaderStyle = {
  padding: "16px 24px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0"
};

const tableActionBtnStyle = {
  background: "none",
  border: "none",
  color: "#4f46e5",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  padding: "4px 8px"
};

export default SkuMaster;
